import { useMemo, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { useDebounce } from "@/hooks/useDebounce";
import type { TamanhoPagina } from "@/hooks/usePaginacaoLocal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, UsersRound, FileUp, Search, FilterX } from "lucide-react";
import { toast } from "sonner";
import ImportarFuncionariosDialog from "@/features/ponto/ImportarFuncionariosDialog";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { ColunaOrdenavel } from "@/features/financeiro/components/ColunaOrdenavel";
import { JornadaSelect } from "@/components/JornadaSelect";
import { DepartamentoSelect, CargoSelect } from "@/components/DepartamentoSelect";
import { UsuarioSearchCombobox } from "@/components/UsuarioSearchCombobox";
import {
  pontoApi, formatarCpf,
  type FuncionarioPonto, type FiltroFuncionarios, type SituacaoFuncionario,
} from "@/features/ponto/pontoApi";

type Ordem = NonNullable<FiltroFuncionarios["ordenarPor"]>;

const VAZIO = {
  Id: 0, Nome: "", SCnpjCpf: "", SMatricula: "", IdJornada: 0, IdUsuarioLogin: 0, IbAtivo: true,
  SPis: "", IdCargo: 0, IdDepartamento: 0, SLocal: "", DtAdmissao: "", DtDemissao: "", DtNascimento: "",
  SRg: "", SSexo: "", STelefone: "", SRegime: "", IbDispensadoPonto: false,
};
const SEM_SEXO = "__none__";
/** "2024-03-01T00:00:00" -> "2024-03-01" (valor do input date). */
const dataInput = (s: string | null) => (s ? s.slice(0, 10) : "");

const SITUACOES: { v: SituacaoFuncionario; t: string }[] = [
  { v: "ativos", t: "Ativos" }, { v: "inativos", t: "Inativos" },
  { v: "cpf-pendente", t: "Ativos com CPF pendente" }, { v: "todos", t: "Todos" },
];

export default function FuncionariosPage() {
  const qc = useQueryClient();
  const [busca, setBusca] = useState("");
  const buscaDebounced = useDebounce(busca, 300);
  const [departamentoId, setDepartamentoId] = useState<number | undefined>();
  const [cargoId, setCargoId] = useState<number | undefined>();
  const [jornadaId, setJornadaId] = useState<number | undefined>();
  const [situacao, setSituacao] = useState<SituacaoFuncionario>("ativos");
  const [ordenarPor, setOrdenarPor] = useState<Ordem>("nome");
  const [direcao, setDirecao] = useState<"asc" | "desc">("asc");
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState<TamanhoPagina>(20);

  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState<any>(VAZIO);
  const [excluir, setExcluir] = useState<FuncionarioPonto | null>(null);
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [jornadaLote, setJornadaLote] = useState<number | undefined>(0);
  const [importar, setImportar] = useState(false);

  const filtro: FiltroFuncionarios = {
    busca: buscaDebounced, departamentoId, cargoId, jornadaId, situacao, ordenarPor, direcao, pagina, tamanhoPagina,
  };
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["ponto-funcionarios-lista", filtro],
    queryFn: () => pontoApi.pesquisarFuncionarios(filtro),
    placeholderData: keepPreviousData,
  });
  const itens = data?.itens ?? [];
  const total = data?.total ?? 0;
  const totalPaginas = Math.max(1, Math.ceil(total / tamanhoPagina));

  // Login vinculado: nome dos usuários da página numa chamada só.
  const idsLogin = useMemo(() => [...new Set(itens.map((f) => f.idUsuarioLogin).filter((id) => id > 0))], [itens]);
  const { data: usuarios = [] } = useQuery({
    queryKey: ["usuarios-lookup-ids", idsLogin],
    queryFn: () => pontoApi.usuarios(undefined, idsLogin),
    enabled: idsLogin.length > 0,
    staleTime: 60_000,
  });
  const loginDe = (id: number) => usuarios.find((u) => u.id === id)?.login;

  const { data: cargos = [] } = useQuery({ queryKey: ["ponto-cargos"], queryFn: () => pontoApi.cargos(false), staleTime: 60_000 });

  const mudarFiltro = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPagina(1); };
  const filtrosAtivos = !!busca || departamentoId !== undefined || cargoId !== undefined || jornadaId !== undefined || situacao !== "ativos";
  const limparFiltros = () => {
    setBusca(""); setDepartamentoId(undefined); setCargoId(undefined); setJornadaId(undefined); setSituacao("ativos"); setPagina(1);
  };

  const ordenar = (campo: Ordem) => {
    if (campo === ordenarPor) setDirecao((d) => (d === "asc" ? "desc" : "asc"));
    else { setOrdenarPor(campo); setDirecao("asc"); }
    setPagina(1);
  };

  const recarregar = () => {
    qc.invalidateQueries({ queryKey: ["ponto-funcionarios-lista"] });
    qc.invalidateQueries({ queryKey: ["ponto-funcionario"] });
    qc.invalidateQueries({ queryKey: ["ponto-funcionarios-busca"] });
    qc.invalidateQueries({ queryKey: ["ponto-cargos"] });
    qc.invalidateQueries({ queryKey: ["ponto-departamentos"] });
  };

  const novo = () => { setForm(VAZIO); setDialog(true); };
  const editar = (f: FuncionarioPonto) => {
    setForm({
      Id: f.id, Nome: f.nome, SCnpjCpf: f.sCnpjCpf, SMatricula: f.sMatricula, IdJornada: f.idJornada, IdUsuarioLogin: f.idUsuarioLogin, IbAtivo: f.ibAtivo,
      SPis: f.sPis, IdCargo: f.idCargo, IdDepartamento: f.idDepartamento, SLocal: f.sLocal,
      DtAdmissao: dataInput(f.dtAdmissao), DtDemissao: dataInput(f.dtDemissao), DtNascimento: dataInput(f.dtNascimento),
      SRg: f.sRg, SSexo: f.sSexo, STelefone: f.sTelefone, SRegime: f.sRegime, IbDispensadoPonto: f.ibDispensadoPonto,
    });
    setDialog(true);
  };

  const escolherCargo = (v: number | undefined) => {
    const id = v ?? 0;
    const cargo = cargos.find((c) => c.id === id);
    // Departamento padrão do cargo entra só se o funcionário ainda não tiver um.
    setForm((f: any) => ({ ...f, IdCargo: id, IdDepartamento: f.IdDepartamento || cargo?.idDepartamento || 0 }));
  };

  const salvar = async () => {
    if (!form.Nome.trim()) { toast.warning("Informe o nome."); return; }
    try {
      await apiClient.post("/api/rh/ponto/funcionarios", {
        ...form,
        // Texto vazio: cargo/departamento vêm só do Id (o nome livre é legado de integração).
        SCargo: "", SDepartamento: "",
        DtAdmissao: form.DtAdmissao || null, DtDemissao: form.DtDemissao || null, DtNascimento: form.DtNascimento || null,
      });
      toast.success("Funcionário salvo."); setDialog(false); recarregar();
    } catch (e: any) { toast.error(e.message ?? "Falha ao salvar."); }
  };

  const confirmarExclusao = async () => {
    if (!excluir) return;
    try {
      const r = await apiClient.delete(`/api/rh/ponto/funcionarios/${excluir.id}`);
      toast.success(r.data?.mensagem ?? "Excluído."); setExcluir(null); recarregar();
    } catch (e: any) { toast.error(e.message ?? "Falha ao excluir."); }
  };

  const toggle = (id: number) => setSel((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const paginaToda = itens.length > 0 && itens.every((f) => sel.has(f.id));
  const togglePagina = () => setSel((s) => {
    const n = new Set(s);
    if (paginaToda) itens.forEach((f) => n.delete(f.id)); else itens.forEach((f) => n.add(f.id));
    return n;
  });
  const selecionarTodosDoFiltro = async () => {
    try {
      const r = await pontoApi.pesquisarFuncionarios({ ...filtro, pagina: 1, tamanhoPagina: 500 });
      setSel(new Set(r.itens.map((f) => f.id)));
    } catch (e: any) { toast.error(e.message ?? "Falha ao selecionar."); }
  };

  const atribuirLote = async () => {
    if (sel.size === 0) { toast.warning("Selecione funcionários."); return; }
    try {
      const r = await apiClient.post("/api/rh/ponto/funcionarios/atribuir-jornada", {
        IdJornada: jornadaLote ?? 0, IdsFuncionarios: [...sel],
      });
      toast.success(r.data?.mensagem ?? "Jornada atribuída.");
      setSel(new Set()); recarregar();
    } catch (e: any) { toast.error(e.message ?? "Falha ao atribuir."); }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold">Funcionários &amp; Escala</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportar(true)}><FileUp className="h-4 w-4 mr-2" />Importar planilha</Button>
          <Button onClick={novo}><Plus className="h-4 w-4 mr-2" />Novo</Button>
        </div>
      </div>

      {/* ── Filtros ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
        <div className="space-y-1 lg:col-span-2">
          <Label>Buscar</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input className="pl-8" placeholder="Nome, matrícula ou CPF" value={busca}
              onChange={(e) => { setBusca(e.target.value); setPagina(1); }} />
          </div>
        </div>
        <div className="space-y-1">
          <Label>Departamento</Label>
          <DepartamentoSelect value={departamentoId} onChange={mudarFiltro(setDepartamentoId)} rotuloTodos="Todos" />
        </div>
        <div className="space-y-1">
          <Label>Cargo</Label>
          <CargoSelect value={cargoId} onChange={mudarFiltro(setCargoId)} rotuloTodos="Todos" />
        </div>
        <div className="space-y-1">
          <Label>Jornada</Label>
          <JornadaSelect value={jornadaId} onChange={mudarFiltro(setJornadaId)} rotuloTodos="Todas" />
        </div>
        <div className="space-y-1">
          <Label>Situação</Label>
          <Select value={situacao} onValueChange={(v) => { setSituacao(v as SituacaoFuncionario); setPagina(1); }}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{SITUACOES.map((s) => <SelectItem key={s.v} value={s.v}>{s.t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {filtrosAtivos && (
        <Button variant="ghost" size="sm" onClick={limparFiltros}><FilterX className="h-4 w-4 mr-2" />Limpar filtros</Button>
      )}

      {sel.size > 0 && (
        <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30 flex-wrap">
          <UsersRound className="h-4 w-4" />
          <span className="text-sm">{sel.size} selecionado(s) — atribuir jornada:</span>
          <JornadaSelect value={jornadaLote} onChange={setJornadaLote} className="w-56" />
          <Button size="sm" onClick={atribuirLote}>Aplicar</Button>
          {sel.size < total && (
            <Button size="sm" variant="link" onClick={selecionarTodosDoFiltro}>Selecionar todos os {total} do filtro</Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setSel(new Set())}>Limpar seleção</Button>
        </div>
      )}

      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox checked={paginaToda} onCheckedChange={togglePagina} aria-label="Selecionar a página" />
              </TableHead>
              <ColunaOrdenavel campo="matricula" ordenarPor={ordenarPor} direcao={direcao} onOrdenar={ordenar}>Matrícula</ColunaOrdenavel>
              <ColunaOrdenavel campo="nome" ordenarPor={ordenarPor} direcao={direcao} onOrdenar={ordenar}>Nome</ColunaOrdenavel>
              <ColunaOrdenavel campo="cpf" ordenarPor={ordenarPor} direcao={direcao} onOrdenar={ordenar}>CPF</ColunaOrdenavel>
              <ColunaOrdenavel campo="cargo" ordenarPor={ordenarPor} direcao={direcao} onOrdenar={ordenar}>Cargo</ColunaOrdenavel>
              <ColunaOrdenavel campo="departamento" ordenarPor={ordenarPor} direcao={direcao} onOrdenar={ordenar}>Departamento</ColunaOrdenavel>
              <ColunaOrdenavel campo="jornada" ordenarPor={ordenarPor} direcao={direcao} onOrdenar={ordenar}>Jornada</ColunaOrdenavel>
              <TableHead>Login (portal)</TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className={isFetching && !isLoading ? "opacity-60" : undefined}>
            {isLoading ? (
              <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground">Carregando…</TableCell></TableRow>
            ) : itens.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground">
                {filtrosAtivos ? "Nenhum funcionário com esses filtros." : "Nenhum funcionário."}
              </TableCell></TableRow>
            ) : itens.map((f) => (
              <TableRow key={f.id} className={!f.ibAtivo ? "opacity-50" : undefined}>
                <TableCell><Checkbox checked={sel.has(f.id)} onCheckedChange={() => toggle(f.id)} /></TableCell>
                <TableCell className="whitespace-nowrap">{f.sMatricula || "—"}</TableCell>
                <TableCell>
                  {f.nome}
                  {!f.ibAtivo && <Badge variant="outline" className="ml-2">inativo</Badge>}
                  {f.menorDeIdade && <Badge variant="secondary" className="ml-2" title="Sem jornada noturna nem hora extra (CLT arts. 404 e 413)">menor</Badge>}
                  {f.ibDispensadoPonto && <Badge variant="outline" className="ml-2">dispensado de ponto</Badge>}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {f.cpfPendente
                    ? <Badge variant="destructive" title="Sem CPF válido não registra ponto">CPF pendente</Badge>
                    : formatarCpf(f.sCnpjCpf)}
                </TableCell>
                <TableCell>{f.sCargo || "—"}</TableCell>
                <TableCell>{f.sDepartamento || "—"}</TableCell>
                <TableCell>
                  {f.idJornada === 0 ? "—" : f.jornadaInativa
                    ? <Badge variant="outline" title="A jornada vinculada foi excluída ou inativada">{f.jornadaNome ? `${f.jornadaNome} (inativa)` : "jornada inativa"}</Badge>
                    : f.jornadaNome}
                </TableCell>
                <TableCell className="text-sm">{f.idUsuarioLogin > 0 ? (loginDe(f.idUsuarioLogin) ?? "…") : <span className="text-muted-foreground">—</span>}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" onClick={() => editar(f)} aria-label="Editar"><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => setExcluir(f)} aria-label="Excluir"><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={pagina}
        totalPaginas={totalPaginas}
        totalItens={total}
        primeiroItem={total === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1}
        ultimoItem={Math.min(pagina * tamanhoPagina, total)}
        tamanhoPagina={tamanhoPagina}
        onPaginaChange={(p) => setPagina(Math.max(1, Math.min(p, totalPaginas)))}
        onTamanhoPaginaChange={(t) => { setTamanhoPagina(t); setPagina(1); }}
      />

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form.Id ? "Editar funcionário" : "Novo funcionário"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1 sm:col-span-2">
              <Label>Nome</Label>
              <Input value={form.Nome} onChange={(e) => setForm({ ...form, Nome: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Matrícula</Label>
              <Input value={form.SMatricula} onChange={(e) => setForm({ ...form, SMatricula: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>CPF</Label>
              <Input value={form.SCnpjCpf} onChange={(e) => setForm({ ...form, SCnpjCpf: e.target.value })} placeholder="000.000.000-00" />
            </div>
            <div className="space-y-1">
              <Label>Cargo</Label>
              <CargoSelect value={form.IdCargo} onChange={escolherCargo} />
            </div>
            <div className="space-y-1">
              <Label>Departamento</Label>
              <DepartamentoSelect value={form.IdDepartamento} onChange={(v) => setForm({ ...form, IdDepartamento: v ?? 0 })} />
            </div>
            <div className="space-y-1">
              <Label>Jornada</Label>
              <JornadaSelect value={form.IdJornada} onChange={(v) => setForm({ ...form, IdJornada: v ?? 0 })} />
            </div>
            <div className="space-y-1">
              <Label>Usuário de login — Portal do Trabalhador</Label>
              <UsuarioSearchCombobox value={form.IdUsuarioLogin} onChange={(id) => setForm({ ...form, IdUsuarioLogin: id })} />
              <p className="text-xs text-muted-foreground">Sem vínculo, o funcionário não acessa o "Meu Ponto".</p>
            </div>
            <div className="space-y-1">
              <Label>PIS</Label>
              <Input value={form.SPis} onChange={(e) => setForm({ ...form, SPis: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Local de trabalho</Label>
              <Input value={form.SLocal} onChange={(e) => setForm({ ...form, SLocal: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Telefone</Label>
              <Input value={form.STelefone} onChange={(e) => setForm({ ...form, STelefone: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Sexo</Label>
              <Select value={form.SSexo || SEM_SEXO} onValueChange={(v) => setForm({ ...form, SSexo: v === SEM_SEXO ? "" : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={SEM_SEXO}>Não informado</SelectItem>
                  <SelectItem value="F">Feminino</SelectItem>
                  <SelectItem value="M">Masculino</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Admissão</Label>
              <Input type="date" value={form.DtAdmissao} onChange={(e) => setForm({ ...form, DtAdmissao: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Demissão</Label>
              <Input type="date" value={form.DtDemissao} onChange={(e) => setForm({ ...form, DtDemissao: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Nascimento</Label>
              <Input type="date" value={form.DtNascimento} onChange={(e) => setForm({ ...form, DtNascimento: e.target.value })} />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Switch checked={form.IbAtivo} onCheckedChange={(v) => setForm({ ...form, IbAtivo: v })} />
              <Label>Ativo</Label>
            </div>
            <div className="flex items-center gap-2 pt-2 sm:col-span-2">
              <Switch checked={form.IbDispensadoPonto} onCheckedChange={(v) => setForm({ ...form, IbDispensadoPonto: v })} />
              <Label>Dispensado de ponto (cargo de gestão)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancelar</Button>
            <Button onClick={salvar}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportarFuncionariosDialog open={importar} onOpenChange={setImportar} onConcluido={recarregar} />

      <AlertDialog open={excluir != null} onOpenChange={(o) => !o && setExcluir(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir {excluir?.nome}?</AlertDialogTitle>
            <AlertDialogDescription>Se houver batidas no histórico, o funcionário é apenas inativado (o ponto é append-only).</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarExclusao}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
